import { useState } from "react";
import type { CMSHeroSection } from "@/mocks/cmsData";

interface Props {
  data: CMSHeroSection;
  onChange: (data: CMSHeroSection) => void;
}

export default function CMSHeroEditor({ data, onChange }: Props) {
  const [lang, setLang] = useState<"ar" | "en">("ar");
  const [newWord, setNewWord] = useState("");

  const update = (field: keyof CMSHeroSection, value: unknown) => {
    onChange({ ...data, [field]: value });
  };

  const currentWords = lang === "ar" ? (data.typewriterWords || []) : (data.typewriterWords_en || []);
  const currentPillars = lang === "ar" ? (data.pillars || []) : (data.pillars_en || []);

  const addWord = () => {
    if (!newWord.trim()) return;
    if (lang === "ar") {
      update("typewriterWords", [...(data.typewriterWords || []), newWord.trim()]);
    } else {
      update("typewriterWords_en", [...(data.typewriterWords_en || []), newWord.trim()]);
    }
    setNewWord("");
  };

  const removeWord = (idx: number) => {
    if (lang === "ar") {
      update("typewriterWords", (data.typewriterWords || []).filter((_, i) => i !== idx));
    } else {
      update("typewriterWords_en", (data.typewriterWords_en || []).filter((_, i) => i !== idx));
    }
  };

  const updateStat = (idx: number, field: "value" | "label" | "label_en", val: string) => {
    const updated = data.stats.map((s, i) => (i === idx ? { ...s, [field]: val } : s));
    update("stats", updated);
  };

  const updatePillar = (idx: number, val: string) => {
    if (lang === "ar") {
      const updated = [...(data.pillars || [])];
      updated[idx] = val;
      update("pillars", updated);
    } else {
      const updated = [...(data.pillars_en || [])];
      updated[idx] = val;
      update("pillars_en", updated);
    }
  };

  return (
    <div className="space-y-6">
      {/* Language Switcher Bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
        <div>
          <h3 className="text-sm font-bold text-gray-800">لغة المحتوى (Content Language)</h3>
          <p className="text-xs text-gray-500">اختر اللغة للتعديل على نصوص الواجهة</p>
        </div>
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setLang("ar")}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
              lang === "ar" ? "bg-white text-[#2E4E45] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            العربية (AR)
          </button>
          <button
            type="button"
            onClick={() => setLang("en")}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
              lang === "en" ? "bg-white text-[#2E4E45] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            English (EN)
          </button>
        </div>
      </div>

      {/* Badge & Heading */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-bold text-[#2E4E45] mb-5 flex items-center gap-2">
          <i className="ri-text-wrap text-base" />
          {lang === "ar" ? "النصوص الرئيسية (العربية)" : "Main Texts (English)"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "الشارة (Badge)" : "Badge (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.badge || "") : (data.badge_en || "")}
              onChange={(e) => update(lang === "ar" ? "badge" : "badge_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "العنوان الرئيسي" : "Main Heading (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.heading1 || "") : (data.heading1_en || "")}
              onChange={(e) => update(lang === "ar" ? "heading1" : "heading1_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "الوصف الرئيسي" : "Main Description (EN)"}
            </label>
            <textarea
              value={lang === "ar" ? (data.description || "") : (data.description_en || "")}
              onChange={(e) => update(lang === "ar" ? "description" : "description_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#2E4E45] transition-colors resize-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "الوصف الثانوي" : "Sub Description (EN)"}
            </label>
            <textarea
              value={lang === "ar" ? (data.subDescription || "") : (data.subDescription_en || "")}
              onChange={(e) => update(lang === "ar" ? "subDescription" : "subDescription_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#2E4E45] transition-colors resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "نص زر الحجز" : "Booking Button Label (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.btnBook || "") : (data.btnBook_en || "")}
              onChange={(e) => update(lang === "ar" ? "btnBook" : "btnBook_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "نص زر الخدمات" : "Services Button Label (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.btnServices || "") : (data.btnServices_en || "")}
              onChange={(e) => update(lang === "ar" ? "btnServices" : "btnServices_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Typewriter Words */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-bold text-[#2E4E45] mb-5 flex items-center gap-2">
          <i className="ri-cursor-line text-base" />
          {lang === "ar" ? "كلمات الكتابة المتحركة (Typewriter AR)" : "Animated Typewriter Words (EN)"}
        </h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {currentWords.map((word, idx) => (
            <div key={idx} className="flex items-center gap-1.5 bg-[#2E4E45]/8 text-[#2E4E45] px-3 py-1.5 rounded-full text-sm font-medium">
              <span>{word}</span>
              <button
                type="button"
                onClick={() => removeWord(idx)}
                className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-red-100 hover:text-red-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addWord()}
            placeholder={lang === "ar" ? "أضف كلمة جديدة..." : "Add new word..."}
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
          />
          <button
            type="button"
            onClick={addWord}
            className="px-4 py-2 bg-[#2E4E45] text-white text-sm rounded-lg hover:bg-[#2E4E45]/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line ml-1" />
            {lang === "ar" ? "إضافة" : "Add"}
          </button>
        </div>
      </div>

      {/* Pillars */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-bold text-[#2E4E45] mb-5 flex items-center gap-2">
          <i className="ri-layout-column-line text-base" />
          {lang === "ar" ? "الركائز الثلاث" : "The Three Pillars (EN)"}
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((idx) => (
            <div key={idx}>
              <label className="block text-xs text-gray-400 mb-1">
                {lang === "ar" ? `ركيزة ${idx + 1}` : `Pillar ${idx + 1}`}
              </label>
              <input
                type="text"
                value={currentPillars[idx] || ""}
                onChange={(e) => updatePillar(idx, e.target.value)}
                dir={lang === "ar" ? "rtl" : "ltr"}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-bold text-[#2E4E45] mb-5 flex items-center gap-2">
          <i className="ri-bar-chart-line text-base" />
          الإحصائيات (Stats)
        </h3>
        <div className="space-y-3">
          {data.stats.map((stat, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">القيمة (Value)</label>
                <input
                  type="text"
                  value={stat.value}
                  onChange={(e) => updateStat(idx, "value", e.target.value)}
                  dir="ltr"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">التسمية بالعربية</label>
                <input
                  type="text"
                  value={stat.label}
                  onChange={(e) => updateStat(idx, "label", e.target.value)}
                  dir="rtl"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Label in English</label>
                <input
                  type="text"
                  value={stat.label_en || ""}
                  onChange={(e) => updateStat(idx, "label_en", e.target.value)}
                  dir="ltr"
                  placeholder="English label..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Background Image */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-bold text-[#2E4E45] mb-5 flex items-center gap-2">
          <i className="ri-image-line text-base" />
          صورة الخلفية
        </h3>
        <div className="flex gap-4 items-start">
          <div className="w-32 h-20 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
            <img src={data.backgroundImage} alt="bg" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">رابط الصورة</label>
            <input
              type="text"
              value={data.backgroundImage}
              onChange={(e) => update("backgroundImage", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
